import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import CardActionArea from "@mui/material/ListItem";
import ListItem from "@mui/material/ListItem";
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { alpha, darken, styled, useTheme } from '@mui/material';

import Article from '@mui/icons-material/Article';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CustomIcon from 'src/components/custom-icon';

// Constantes de design
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const TRANSITION_DURATION = '0.25s';
const BORDER_COLOR = '#f0f0f0';

const baseTransition = `all ${TRANSITION_DURATION} ${TRANSITION_EASING}`;

// Estilos base reutilizáveis
const truncateText = (lines = 2) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical'
});

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.1rem'
  },
  color: '#000',
  lineHeight: 1.4,
  transition: `color ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
  ...truncateText(2)
}));

const CardContainer = styled(CardActionArea, {
  shouldForwardProp: (prop) => !['accentColor', 'darkenAccentColor', 'isLast'].includes(prop)
})(({ theme, accentColor, darkenAccentColor, isLast }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5, 2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3, 3)
  },
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
  margin: 8,
  transition: baseTransition,
  position: 'relative',
  overflow: 'hidden',
  borderBottom: isLast ? 'none' : `1px solid ${BORDER_COLOR}`,

  // Indicador lateral
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 4,
    height: '0%',
    backgroundColor: darkenAccentColor,
    borderRadius: '0 4px 4px 0',
    transition: `height ${TRANSITION_DURATION} ${TRANSITION_EASING}`
  },

  // Efeitos de hover, foco e ativo
  '&:hover': {
    textDecoration: 'none',
    backgroundColor: alpha(darken(accentColor, 0.2), 0.1),
    '&::before': { height: '70%' },
    '& .article-icon-container': {
      transform: 'scale(1.05)',
      '& .article-icon': { transform: 'scale(1.05)' }
    },
    '& .article-title': { color: darken(accentColor, 0.3) },
    '& .article-arrow': { opacity: 1, transform: 'translateX(4px)' }
  },

  '&:focus-visible': {
    outline: 'none',
    textDecoration: 'none',
    backgroundColor: alpha(accentColor, 0.06),
    '&::before': { height: '90%' }
  },

  '&:active': {
    outline: 'none',
    textDecoration: 'none',
    backgroundColor: alpha(accentColor, 0.08),
    '&::before': { height: '100%' }
  },

  '&:visited': {
    color: 'inherit'
  }
}));

// Componente estilizado para o container do ícone
const IconContainer = styled(Box)(({ theme }) => ({
  width: 44,
  height: 44,
  [theme.breakpoints.up('sm')]: {
    width: 52,
    height: 52
  },
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.text.secondary, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    marginRight: theme.spacing(2.5)
  },
  flexShrink: 0,
  transition: baseTransition
}));

export default function NewsSectionCard({ article, index, articles, clientportal, props }) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  // Cor de destaque (memoizada para evitar recálculos)
  const accentColor = useMemo(
    () => clientportal.defaultcolor || primaryColor,
    [clientportal.defaultcolor, primaryColor]
  );

  const darkenAccentColor = useMemo(
    () => darken(accentColor, 0.09),
    [accentColor]
  );

  return (
    <ListItem
      key={article.id}
      disablePadding
    >
      <CardContainer
        component={Link}
        to={`/${props.match.params.lang}/article-external/${article.id}`}
        accentColor={accentColor}
        darkenAccentColor={darkenAccentColor}
        isLast={index === articles.length - 1}
      >
        {/* Ícone do artigo */}
        <IconContainer className="article-icon-container">
          <CustomIcon
            icon={Article}
            color={darkenAccentColor}
            iconSx={{
              fontSize: { xs: 22, sm: 26 },
              transition: `color ${TRANSITION_DURATION} ${TRANSITION_EASING}`
            }}
          />
        </IconContainer>

        {/* Título e número */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Title className="article-title" variant="h6">
            {article.title}
          </Title>
        </Box>

        {/* Seta indicadora */}
        <CustomIcon
          icon={ChevronRight}
          className="article-arrow"
          color={darkenAccentColor}
          iconSx={{
            fontSize: { xs: 20, sm: 24 },
            opacity: 0.3,
            ml: 1,
            flexShrink: 0,
            transition: baseTransition
          }}
        />
      </CardContainer>
    </ListItem>
  )
}